/**
 * EIR file browser. An accordion list of assigned founders that expands to show
 * each founder's signed client agreement (VRCA, with version history) and
 * general documents, loaded lazily per founder via getFounderFiles.
 *
 * Read-only: EIRs can preview and download but not upload or delete.
 */
import React, { useEffect, useState } from 'react';
import { getMyFounders, getFounderFiles } from '../services/apiService';

// Short, human-readable date (e.g. "Jan 5, 2026"); em dash when absent.
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Fetch the file (with cookies) and force a client-side download under the
// given filename, rather than navigating to the (possibly inline-rendered) URL.
function triggerBlobDownload(url, filename) {
  fetch(url, { credentials: 'include' })
    .then((r) => r.blob())
    .then((blob) => {
      const burl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = burl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(burl);
    })
    .catch(() => {});
}

function IconDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconEye() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconChevron({ open }) {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function FileRow({ f, showVersionBadge }) {
  return (
    <div style={styles.fileRow}>
      {showVersionBadge && (
        <span style={styles.versionBadge}>Current · v{f.version_number}</span>
      )}
      <span style={styles.fileName}>{f.original_filename}</span>
      <span style={styles.fileMeta}>{formatDate(f.uploaded_at)}</span>
      <button style={styles.iconBtn} onClick={() => window.open(f.url, '_blank')}>
        <IconEye /><span>Preview</span>
      </button>
      <button style={styles.iconBtn} onClick={() => triggerBlobDownload(f.url, f.original_filename)}>
        <IconDownload /><span>Download</span>
      </button>
    </div>
  );
}

// Lazily loads and renders one founder's files: current VRCA with collapsible
// version history, plus general documents.
function FounderFilesPanel({ founder }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  useEffect(() => {
    getFounderFiles(founder.id)
      .then(setData)
      .finally(() => setLoading(false));
  }, [founder.id]);

  if (loading) return <p style={styles.dim}>Loading files…</p>;
  if (!data) return <p style={styles.dim}>Failed to load files.</p>;

  const vrcaCurrent = data.vrca?.current;
  const historicalVersions = (data.vrca?.versions || []).filter((v) => !v.is_current);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* VRCA */}
      <div>
        <h3 style={styles.panelSectionTitle}>Venture Readiness Client Agreement</h3>
        {vrcaCurrent ? (
          <>
            <FileRow f={vrcaCurrent} showVersionBadge />
            {historicalVersions.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button style={styles.versionToggle} onClick={() => setShowVersionHistory((v) => !v)}>
                  {showVersionHistory ? 'Hide' : 'Show'} version history ({historicalVersions.length})
                </button>
                {showVersionHistory && (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {historicalVersions.map((v) => (
                      <div key={v.id} style={{ ...styles.fileRow, background: 'rgba(255,255,255,0.5)' }}>
                        <span style={{ ...styles.fileMeta, minWidth: 28 }}>v{v.version_number}</span>
                        <span style={styles.fileName}>{v.original_filename}</span>
                        <span style={styles.fileMeta}>{formatDate(v.uploaded_at)}</span>
                        <button style={styles.iconBtn} onClick={() => window.open(v.url, '_blank')}>
                          <IconEye /><span>Preview</span>
                        </button>
                        <button style={styles.iconBtn} onClick={() => triggerBlobDownload(v.url, v.original_filename)}>
                          <IconDownload /><span>Download</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <p style={{ ...styles.dim, margin: '6px 0 0' }}>No agreement uploaded yet.</p>
        )}
      </div>

      {/* General Documents */}
      <div>
        <h3 style={styles.panelSectionTitle}>Documents</h3>
        {data.general.length === 0 ? (
          <p style={{ ...styles.dim, margin: '6px 0 0' }}>No documents uploaded.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.general.map((f) => (
              <FileRow key={f.id} f={f} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

/** Accordion of assigned founders exposing their agreements and documents. */
export default function EIRFiles() {
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    getMyFounders()
      .then(setFounders)
      .finally(() => setLoading(false));
  }, []);

  function toggleFounder(id) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <h1 style={styles.title}>Files</h1>
          <p style={styles.subtitle}>View and download documents for your assigned founders.</p>
        </div>

        {loading && <p style={styles.dim}>Loading…</p>}

        {!loading && founders.length === 0 && (
          <p style={styles.dim}>No founders assigned yet.</p>
        )}

        {!loading && founders.length > 0 && (
          <div style={styles.table}>
            {founders.map((founder, idx) => {
              const isOpen = expandedId === founder.id;
              const isLast = idx === founders.length - 1;
              return (
                <div key={founder.id} style={{ borderBottom: isLast ? 'none' : '1px solid var(--color-mint)' }}>
                  <button
                    style={{ ...styles.tableRow, ...(isOpen ? styles.tableRowActive : {}) }}
                    onClick={() => toggleFounder(founder.id)}
                  >
                    <div style={styles.colName}>
                      <p style={styles.rowName}>{founder.first_name} {founder.last_name}</p>
                      {founder.company_name && (
                        <p style={styles.rowCompany}>{founder.company_name}</p>
                      )}
                    </div>
                    <IconChevron open={isOpen} />
                  </button>

                  {isOpen && (
                    <div style={styles.panelBody}>
                      <FounderFilesPanel founder={founder} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  page: {
    flex: 1,
    overflowY: 'auto',
    background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed',
    padding: 'var(--space-8)',
  },
  inner: { maxWidth: '98%', margin: '0 auto' },
  header: { marginBottom: 'var(--space-6)', paddingTop: 'var(--space-4)' },
  title: {
    fontFamily: 'var(--font-accent)',
    fontSize: 64,
    fontWeight: 500,
    color: 'var(--color-rust)',
    lineHeight: 1.1,
    marginBottom: 'var(--space-2)',
  },
  subtitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 22,
    fontWeight: 'var(--font-weight-page-subtitle)',
    color: 'var(--color-forest)',
    opacity: 0.6,
    lineHeight: 1.5,
  },
  dim: {
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    color: 'var(--color-forest)',
    opacity: 0.5,
    fontStyle: 'italic',
  },
  table: {
    background: 'var(--color-frost)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-card)',
    overflow: 'hidden',
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '16px 24px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    color: 'var(--color-forest)',
    transition: 'background 0.12s',
  },
  tableRowActive: {
    background: 'var(--color-mint)',
  },
  colName: { minWidth: 0 },
  rowName: {
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--color-forest)',
    margin: 0,
  },
  rowCompany: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-forest)',
    opacity: 0.6,
    margin: '2px 0 0',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  panelBody: {
    padding: '20px 24px 28px',
    borderTop: '1px solid var(--color-mint)',
    background: 'var(--color-mint-dark)',
  },
  panelSectionTitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--color-forest)',
    margin: '0 0 10px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    opacity: 0.7,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    background: 'var(--color-frost)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-mint-dark)',
  },
  fileName: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--color-forest)',
    fontFamily: 'var(--font-body)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileMeta: {
    fontSize: 12,
    color: 'var(--color-forest)',
    opacity: 0.55,
    fontFamily: 'var(--font-body)',
    whiteSpace: 'nowrap',
  },
  versionBadge: {
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 9999,
    background: '#dcfce7',
    color: '#15803d',
    fontFamily: 'var(--font-body)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  iconBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 11px',
    borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--color-mint-dark)',
    background: '#fff',
    color: 'var(--color-forest)',
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  versionToggle: {
    background: 'none',
    border: 'none',
    color: 'var(--color-forest)',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    opacity: 0.65,
    textDecoration: 'underline',
  },
};
